























































import { Component, Prop, Watch, Mixins } from "vue-property-decorator";
import { IBrandGateway, IGateway } from "@/models/gateways";
import HasValidation from "@/mixins/HasValidation";
import { ValidationObserver } from "vee-validate";
import {
  GatewayCardData,
  PaymentMethodType,
  SelectedPaymentMethod,
  SelectedPaymentMethodGatewayCard
} from "@/models/selectPaymentMethod";
import CreditCardForm from "@/components/app/global/payments/creditCardForm.vue";
import { GatewayContext, GatewayStoreType } from "@/data/enums/gateway";
import { IClient } from "@/models/clients";
import { IAddress } from "@/models/addresses";
import { BrandConfigKeys } from "@/data/constants";
import Context from "@/mixins/context";

interface ICardForm {
  address_id: null | IAddress["id"];
  auto_payment: boolean;
  card_cvv: string;
  card_expire_date: string;
  card_num: string;
  card_type: string;
  cardholder_name: string;
  name: string;
  store: boolean;
}

@Component({
  components: {
    CreditCardForm,
    "client-address-select": () =>
      import("@/components/app/global/fields/clientAddressSelect.vue")
  }
})
export default class CardGateway extends Mixins(HasValidation, Context) {
  public declare $refs: {
    creditCardForm: CreditCardForm;
    observer: InstanceType<typeof ValidationObserver>;
  };
  // Props
  @Prop({ required: true })
  public value!: SelectedPaymentMethod | null;

  @Prop({ required: true })
  public clientId!: IClient["id"];

  @Prop({ default: "" })
  public cardHolderName!: string;

  @Prop({ required: true })
  public brandGateway!: IBrandGateway;

  @Prop({ default: false })
  public isDisabled!: boolean;

  @Prop({ default: true })
  public showAddressField!: boolean;

  @Prop({ required: true })
  public gatewayContext!: GatewayContext;

  // Data

  public form: ICardForm = {
    address_id: null,
    auto_payment: this.canStoreCard,
    card_cvv: "",
    card_expire_date: "",
    card_num: "",
    card_type: "",
    cardholder_name: "",
    name: "",
    store: this.canStoreCard
  };

  public created() {
    if (!this.brandGateway.gateway.gateway_provider.requires_name) {
      this.$delete(this.form, "cardholder_name");
    } else {
      this.$set(this.form, "cardholder_name", this.cardHolderName);
    }

    return this.$emit("input", this.isExternal ? this.externalCardData : null);
  }

  public get isAddContext() {
    return this.gatewayContext === GatewayContext.ADD;
  }

  public get isPayContext() {
    return this.gatewayContext === GatewayContext.PAY;
  }

  public get externalCardData(): SelectedPaymentMethodGatewayCard {
    return {
      type: PaymentMethodType.GATEWAY_CARD,
      data: {
        external: true,
        requires_address: this.requiresAddress,
        gateway_id: this.brandGateway.gateway_id
      }
    };
  }

  public get cardData(): SelectedPaymentMethodGatewayCard {
    return {
      type: PaymentMethodType.GATEWAY_CARD,
      data: {
        ...this.computedForm,
        external: false
      } as GatewayCardData
    };
  }

  public get computedForm(): ICardForm & {
    gateway_id: IGateway["id"];
    requires_address: boolean;
  } {
    return {
      ...this.form,
      card_num: this.$_.get(this.form, "card_num", "").replace(/\s/g, ""),
      gateway_id: this.$_.get(this.brandGateway, "gateway_id"),
      requires_address: this.requiresAddress
    };
  }

  /**
   * @name forceCardStorage
   * @desc Returns true if detail storage is enforced by the brand
   */

  public get forceCardStorage(): boolean {
    return (
      (this.$store.getters["brand/config"][
        BrandConfigKeys.BILLING_GATEWAY_FORCE_CARD_STORAGE
      ] ??
        false) ||
      ((this.isPayContext &&
        this.brandGateway.gateway?.gateway_provider?.store_on_payment_force) ??
        false)
    );
  }

  /**
   * @name forceAutoPayment
   * @desc Returns true if uatopayment is enforced by the brand
   */

  public get forceAutoPayment(): boolean {
    return (
      this.$store.getters["brand/config"][
        BrandConfigKeys.BILLING_GATEWAY_FORCE_AUTO_PAYMENT
      ] ?? false
    );
  }

  /**
   * @name isExternal
   * @desc Returns true if payment is processed off-site via a redirect flow
   * @return Boolean
   */

  public get isExternal(): boolean {
    return !!this.brandGateway.gateway?.gateway_provider?.external_payment;
  }

  public get requiresAddress(): boolean {
    return this.$_.get(
      this.brandGateway,
      "gateway.gateway_provider.needs_address",
      false
    );
  }

  public get canStoreCard() {
    // Return false for ALL guests
    if (this.isGuest) return false;
    // Return false if explicitly disabled during gateway setup
    if (!this.brandGateway?.gateway?.is_stored) return false;
    // Otherwise, rely on provider `store_type` value
    return !!(
      !this.brandGateway?.gateway?.gateway_provider?.external_store &&
      [(GatewayStoreType.ALWAYS, GatewayStoreType.EITHER)].includes(
        this.brandGateway?.gateway.gateway_provider?.store_type
      )
    );
  }

  // Methods

  @Watch("computedForm", { deep: true })
  public computedFormChanged() {
    this.$nextTick(() => {
      if (this.isExternal) {
        return this.$emit("input", this.externalCardData);
      }
      // fix for the initial emit with empty card form
      if (!this.$refs.creditCardForm) {
        return this.$emit("input", null);
      }
      this.validate(true).then(isValid => {
        this.$emit("input", isValid ? this.cardData : null);
      });
    });
  }
}
